import {
  Download as DownloadIcon,
  DownloadCloud,
  WifiOff,
  Zap,
  Activity,
  Lock,
  ShieldCheck,
  EyeOff,
  Plus,
  Minus,
  Monitor,
  Ghost,
  AlertCircle,
  FileText,
} from "lucide-react";
import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { AnimatePresence, motion } from "framer-motion";
import { fetchLatestDownloads } from "../../utils/dynamicDownloadManager";
import { AppleLogo, WindowsLogo, LinuxLogo } from "./osIcons";
import "./DownloadAll.css";
import PublicHeader from "../../components/PublicHeader/PublicHeader";
import Footer from "../../components/Footer/Footer";
import ReadyToTransform from "../../components/ReadyToTransform/ReadyToTransform";

const TEAL = "#00D1C1";
const AGENT_BASE = "/api/v1/stealth-agent/downloads";

// The backend presigns and 302-redirects at /downloads/<key> using its own
// credentials, so there is no client-side S3 path here. A dead browser-side
// presign fallback used to live here from before that route shipped; it needed
// AWS keys in the bundle, so it was removed.
async function resolveAgentDownloadUrl(key) {
  const apiUrl = `${AGENT_BASE}/${key}`;
  try {
    const res = await fetch(apiUrl, { method: "GET", redirect: "manual" });
    if (res.status === 301 || res.status === 302 || res.status === 303 || res.status === 307) {
      const loc = res.headers.get("Location");
      if (loc) return loc;
    }
    if (res.ok) return apiUrl;
  } catch {
    /* fall through — the browser will follow the API URL itself */
  }
  return apiUrl;
}

// Layman install guides (PDF), served from the frontend's public/guides folder.
const AGENT_GUIDE = {
  windows: "/guides/TeamGrid-Background-Agent-Windows.pdf",
  mac: "/guides/TeamGrid-Background-Agent-macOS.pdf",
  linux: "/guides/TeamGrid-Background-Agent-Linux.pdf",
};

const FAQ_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: [
    {
      "@type": "Question",
      name: "What is the difference between the Desktop App and the Background Agent?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "The Desktop App is the full TeamGrid application with a tray icon, dashboard access, offline mode and manual controls. The Background Agent is a lightweight installer that runs silently and reports activity to your workspace. Install whichever your workspace administrator recommends.",
      },
    },
    {
      "@type": "Question",
      name: "How do I install TeamGrid?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Pick your product and operating system, download the installer, and follow the on-screen steps. The Background Agent on Windows and macOS uses a one-click installer (.exe / .dmg).",
      },
    },
  ],
};

// Entrance animation presets (respect reduced motion via CSS fallback).
const fadeUp = {
  hidden: { opacity: 0, y: 22 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.5, delay: i * 0.07, ease: [0.2, 0.7, 0.2, 1] } }),
};
const viewport = { once: true, amount: 0.2 };

const DownloadAllPage = () => {
  const [product, setProduct] = useState("desktop"); // "desktop" | "agent"
  const [status, setStatus] = useState({});
  const [urls, setUrls] = useState({});
  const [versions, setVersions] = useState({});
  const [loading, setLoading] = useState(true);
  const [faqOpen, setFaqOpen] = useState({ 0: true });

  const toggleFaq = (i) => setFaqOpen((p) => ({ ...p, [i]: !p[i] }));

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const downloads = await fetchLatestDownloads();
        const u = {};
        const v = {};
        Object.keys(downloads).forEach((p) => {
          u[p] = downloads[p].url;
          v[p] = downloads[p].version;
        });
        setUrls(u);
        setVersions(v);
      } catch (err) {
        const b = "https://teamgrid.ai/downloads";
        setUrls({
          windows: `${b}/TeamGrid%20Setup%202.0.4.exe`,
          mac: `${b}/TeamGrid-2.0.0.dmg`,
          "mac-intel": `${b}/TeamGrid-2.0.0.dmg`,
          "mac-arm64": `${b}/TeamGrid-2.0.0-arm64.dmg`,
          ubuntu: `${b}/team-grid-os_2.0.2_amd64.deb`,
          "ubuntu-arm64": `${b}/team-grid-os_2.0.2_amd64.deb`,
        });
        setVersions({ windows: "2.0.4", "mac-arm64": "2.0.0", "mac-intel": "2.0.0", ubuntu: "2.0.2" });
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const trigger = (href, name) => {
    const a = document.createElement("a");
    a.href = href;
    if (name) a.download = name;
    a.rel = "noopener noreferrer";
    a.style.display = "none";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const dlDesktop = (platform) => {
    if (!urls[platform]) return alert("Download link not available for this platform");
    setStatus((s) => ({ ...s, [platform]: "downloading" }));
    const parts = urls[platform].split("/");
    trigger(urls[platform], decodeURIComponent(parts[parts.length - 1] || `TeamGrid-${platform}`));
    setTimeout(() => setStatus((s) => ({ ...s, [platform]: "success" })), 1500);
  };

  const dlAgent = async (key) => {
    const k = `agent-${key}`;
    setStatus((s) => ({ ...s, [k]: "downloading" }));
    try {
      const href = await resolveAgentDownloadUrl(key);
      // Do not set a.download — cross-origin S3 redirects break Chrome ("file wasn't available").
      const a = document.createElement("a");
      a.href = href;
      a.rel = "noopener noreferrer";
      a.style.display = "none";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => setStatus((s) => ({ ...s, [k]: "success" })), 1500);
    } catch (err) {
      console.error("[DownloadAll] agent download failed", err);
      setStatus((s) => ({ ...s, [k]: "error" }));
      alert("Download not available right now. Please try again in a moment.");
    }
  };

  const displayVersion = versions.windows || versions["mac-arm64"] || versions.ubuntu || "2.0.4";

  const features = [
    { icon: WifiOff, title: "Offline Mode", desc: "Keeps tracking when the connection drops, then syncs automatically the moment you're back online." },
    { icon: Zap, title: "Zero Latency", desc: "Native performance runs quietly in the background without ever slowing your machine down." },
    { icon: Activity, title: "Auto-Pause", desc: "Smart idle detection pauses tracking the moment you step away, and resumes when you return." },
  ];

  const desktopSteps = [
    { n: 1, title: "Download & Open", desc: "Grab the installer for your OS and double-click to launch the setup wizard." },
    { n: 2, title: "Sign In", desc: "Log in with your TeamGrid credentials. SSO is supported on Enterprise plans." },
    { n: 3, title: "Start Tracking", desc: "The app minimizes to your tray. Work as usual, and we handle the rest." },
  ];
  const agentSteps = [
    { n: 1, title: "Download", desc: "Choose the agent for your operating system from the cards above." },
    { n: 2, title: "Run the installer", desc: "On macOS, open the .dmg and click Install. A sign-in window opens automatically." },
    { n: 3, title: "Sign in once", desc: "Sign in once with your work account. You won't be asked again." },
  ];
  const steps = product === "desktop" ? desktopSteps : agentSteps;

  const security = [
    { icon: ShieldCheck, title: "Bank-Level Security", sub: "SOC 2 Type II Compliant" },
    { icon: Lock, title: "End-to-End Encryption", sub: "Encrypted at rest and in transit" },
    { icon: EyeOff, title: "Privacy Controls", sub: "User-controlled data visibility" },
  ];

  const faqs = [
    {
      q: "What's the difference between the Desktop App and the Background Agent?",
      a: "The Desktop App is the full TeamGrid application with a tray icon, dashboard access, offline mode and manual start/stop. The Background Agent is a lightweight installer that runs silently and reports to your workspace. Install whichever your workspace administrator recommends.",
    },
    {
      q: "How do I install TeamGrid?",
      a: "Pick your product and operating system above, download the installer, and follow the on-screen steps. The Desktop App is a double-click wizard; the Background Agent on macOS uses a one-click .dmg installer.",
    },
    {
      q: "What are the system requirements?",
      a: "We support macOS 11.0+, Windows 10/11 (64-bit), and most modern Linux distributions (Ubuntu, Fedora, Debian). You'll need an internet connection for the first sign-in.",
    },
  ];

  const heroSub =
    product === "desktop"
      ? "The full desktop application, with automatic time tracking, offline mode, and zero-latency AI insights."
      : "A lightweight agent that installs in minutes and runs quietly in the background, reporting to your workspace.";

  return (
    <>
      <Helmet>
        <title>Download TeamGrid | Desktop App & Background Agent</title>
        <meta
          name="description"
          content="Download TeamGrid for Windows, macOS, or Linux — the full Desktop App or the lightweight Background Agent, all in one place."
        />
        <link rel="canonical" href="https://teamgrid.ai/download/all" />
        <script type="application/ld+json">{JSON.stringify(FAQ_SCHEMA)}</script>
      </Helmet>

      <div className="dla" data-page="download-all">
        <PublicHeader />
        <div className="pt-16 sm:pt-18 md:pt-20">
          {/* ── HERO ── */}
          <section className="dla-hero">
            <motion.div className="dla-hero-inner" initial="hidden" animate="show" variants={{ show: { transition: { staggerChildren: 0.08 } } }}>
              <motion.div variants={fadeUp}>
                <span className="dla-badge">
                  <span className="dla-badge-dot" />
                  <DownloadCloud size={13} stroke={TEAL} />
                  {product === "desktop" ? `Desktop App · Version ${displayVersion}` : "Background Agent · Lightweight"}
                </span>
              </motion.div>

              <motion.h1 className="dla-title" variants={fadeUp}>
                Download <span className="dla-grad">TeamGrid</span>
              </motion.h1>

              <motion.p className="dla-hero-sub" variants={fadeUp}>
                {heroSub}
              </motion.p>

              <motion.div variants={fadeUp}>
                <div className="dla-toggle" role="tablist" aria-label="Choose a TeamGrid product">
                  {[
                    { id: "desktop", label: "Desktop App", Icon: Monitor },
                    { id: "agent", label: "Background Agent", Icon: Ghost },
                  ].map(({ id, label, Icon }) => (
                    <button
                      key={id}
                      type="button"
                      role="tab"
                      aria-selected={product === id}
                      className={`dla-toggle-btn ${product === id ? "is-active" : ""}`}
                      onClick={() => setProduct(id)}
                    >
                      {product === id && (
                        <motion.span layoutId="dla-toggle-pill" className="dla-toggle-pill" transition={{ type: "spring", stiffness: 380, damping: 32 }} />
                      )}
                      <Icon size={16} />
                      <span>{label}</span>
                    </button>
                  ))}
                </div>
              </motion.div>
            </motion.div>

            {/* ── CARDS ── */}
            <AnimatePresence mode="wait">
              {product === "desktop" ? (
                <motion.div
                  key="desktop"
                  className="dla-cards cols-3"
                  initial="hidden"
                  animate="show"
                  exit={{ opacity: 0, y: 10 }}
                  variants={{ show: { transition: { staggerChildren: 0.09, delayChildren: 0.05 } } }}
                >
                  {/* macOS */}
                  <motion.div className="dla-card" variants={fadeUp}>
                    <div className="dla-icon mac"><AppleLogo /></div>
                    <h3 className="dla-card-title">TeamGrid for macOS</h3>
                    <p className="dla-card-req">Requires macOS 11.0 or later</p>
                    <div className="dla-card-actions">
                      <button className="dla-btn dla-btn-outline" onClick={() => dlDesktop("mac-arm64")} disabled={loading || status["mac-arm64"] === "downloading"}>
                        <span>Apple Silicon</span>
                        <span className="dla-chip teal">M1/M2/M3</span>
                        <DownloadIcon size={16} className="dla-btn-ico-right" />
                      </button>
                      <button className="dla-btn dla-btn-outline" onClick={() => dlDesktop("mac-intel")} disabled={loading || status["mac-intel"] === "downloading"}>
                        <span>Intel Processor</span>
                        <DownloadIcon size={16} className="dla-btn-ico-right" />
                      </button>
                    </div>
                    <button className="dla-link" onClick={() => alert("Apple menu → About This Mac. If it says a chip like M1/M2/M3, choose Apple Silicon.")}>
                      Not sure which processor?
                    </button>
                  </motion.div>

                  {/* Windows */}
                  <motion.div className="dla-card is-featured" variants={fadeUp}>
                    <div className="dla-icon windows"><WindowsLogo /></div>
                    <h3 className="dla-card-title">TeamGrid for Windows</h3>
                    <p className="dla-card-req">Windows 10 &amp; 11 (64-bit)</p>
                    <div className="dla-card-actions">
                      <button className="dla-btn dla-btn-primary" onClick={() => dlDesktop("windows")} disabled={loading || status.windows === "downloading"}>
                        <DownloadIcon size={18} />
                        {loading ? "Loading..." : status.windows === "downloading" ? "Downloading..." : status.windows === "success" ? "Downloaded!" : "Download for Windows"}
                      </button>
                    </div>
                    <div className="dla-card-features">
                      <span>✓ Auto-updates</span>
                      <span>✓ 64-bit</span>
                    </div>
                    <div className="dla-card-note">
                      <AlertCircle size={16} />
                      <p><strong>Note:</strong> uninstall the current build, remove its <strong>Program Files</strong> folder and clear app data before installing this version.</p>
                    </div>
                  </motion.div>

                  {/* Linux */}
                  <motion.div className="dla-card" variants={fadeUp}>
                    <div className="dla-icon linux"><LinuxLogo /></div>
                    <h3 className="dla-card-title">TeamGrid for Linux</h3>
                    <p className="dla-card-req">Ubuntu 20.04+, Fedora, Debian</p>
                    <div className="dla-card-actions">
                      <button className="dla-btn dla-btn-outline" onClick={() => dlDesktop("ubuntu")} disabled={loading || status.ubuntu === "downloading"}>
                        <span>.deb Package</span>
                        <span className="dla-chip ubuntu">Ubuntu</span>
                        <DownloadIcon size={16} className="dla-btn-ico-right" />
                      </button>
                      <button className="dla-btn dla-btn-outline" onClick={() => dlDesktop("ubuntu-arm64")} disabled={loading || status["ubuntu-arm64"] === "downloading"}>
                        <span>.AppImage</span>
                        <DownloadIcon size={16} className="dla-btn-ico-right" />
                      </button>
                    </div>
                    <button className="dla-link">View checksums</button>
                  </motion.div>
                </motion.div>
              ) : (
                <motion.div key="agent" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 10 }} transition={{ duration: 0.35 }}>
                  <motion.div
                    className="dla-cards cols-3"
                    initial="hidden"
                    animate="show"
                    variants={{ show: { transition: { staggerChildren: 0.09, delayChildren: 0.05 } } }}
                  >
                    {/* Windows — one-click Setup EXE */}
                    <motion.div className="dla-card is-featured" variants={fadeUp}>
                      <div className="dla-icon windows"><WindowsLogo /></div>
                      <h3 className="dla-card-title">Agent for Windows</h3>
                      <p className="dla-card-req">One-click installer · Windows 10 &amp; 11 · 64-bit</p>
                      <div className="dla-card-actions">
                        <button className="dla-btn dla-btn-primary" onClick={() => dlAgent("windows-installer")} disabled={status["agent-windows-installer"] === "downloading"}>
                          <DownloadIcon size={17} />
                          {status["agent-windows-installer"] === "downloading" ? "Starting..." : status["agent-windows-installer"] === "success" ? "Downloaded!" : "Download installer"}
                        </button>
                      </div>
                      <div className="dla-card-meta"><span className="dla-meta-pill">.exe</span><span>≈ 2 MB</span></div>
                      <a className="dla-guide" href={AGENT_GUIDE.windows} target="_blank" rel="noopener noreferrer">
                        <FileText size={13} /> Installation guide (PDF)
                      </a>
                    </motion.div>

                    {/* macOS — Apple Silicon + Intel (one-click DMG) */}
                    <motion.div className="dla-card is-featured" variants={fadeUp}>
                      <div className="dla-icon mac"><AppleLogo /></div>
                      <h3 className="dla-card-title">Agent for macOS</h3>
                      <p className="dla-card-req">One-click installer · macOS 11+</p>
                      <div className="dla-card-actions">
                        <button className="dla-btn dla-btn-outline" onClick={() => dlAgent("mac-installer")} disabled={status["agent-mac-installer"] === "downloading"}>
                          <span>Apple Silicon</span>
                          <span className="dla-chip teal">M1/M2/M3</span>
                          <DownloadIcon size={16} className="dla-btn-ico-right" />
                        </button>
                        <button className="dla-btn dla-btn-outline" onClick={() => dlAgent("mac-intel-installer")} disabled={status["agent-mac-intel-installer"] === "downloading"}>
                          <span>Intel Processor</span>
                          <DownloadIcon size={16} className="dla-btn-ico-right" />
                        </button>
                      </div>
                      <div className="dla-card-meta"><span className="dla-meta-pill">.dmg</span><span>≈ 3 MB</span></div>
                      <a className="dla-guide" href={AGENT_GUIDE.mac} target="_blank" rel="noopener noreferrer">
                        <FileText size={13} /> Installation guide (PDF)
                      </a>
                    </motion.div>

                    {/* Linux */}
                    <motion.div className="dla-card" variants={fadeUp}>
                      <div className="dla-icon linux"><LinuxLogo /></div>
                      <h3 className="dla-card-title">Agent for Linux</h3>
                      <p className="dla-card-req">.zip · extract &amp; run</p>
                      <div className="dla-card-actions">
                        <button className="dla-btn dla-btn-primary" onClick={() => dlAgent("linux")} disabled={status["agent-linux"] === "downloading"}>
                          <DownloadIcon size={17} />
                          {status["agent-linux"] === "downloading" ? "Starting..." : status["agent-linux"] === "success" ? "Downloaded!" : "Download"}
                        </button>
                      </div>
                      <div className="dla-card-meta"><span className="dla-meta-pill">.zip</span><span>≈ 40 MB</span></div>
                      <a className="dla-guide" href={AGENT_GUIDE.linux} target="_blank" rel="noopener noreferrer">
                        <FileText size={13} /> Installation guide (PDF)
                      </a>
                    </motion.div>
                  </motion.div>
                </motion.div>
              )}
            </AnimatePresence>
          </section>

          {/* ── WHICH ONE ── */}
          <section className="dla-section">
            <div className="dla-wrap">
              <motion.div className="dla-section-head" initial="hidden" whileInView="show" viewport={viewport} variants={fadeUp}>
                <span className="dla-eyebrow">Not sure?</span>
                <h2 className="dla-h2" style={{ marginTop: 10 }}>Which one do I need?</h2>
              </motion.div>
              <div className="dla-compare-grid">
                {[
                  { id: "desktop", Icon: Monitor, title: "Desktop App", desc: "Full application with a tray icon, dashboard access, offline mode and manual start/stop. Installs with a double-click wizard. Best for individuals and teams who want visibility and control.", tags: ["Windows", "macOS", "Linux", ".exe / .dmg / .deb"] },
                  { id: "agent", Icon: Ghost, title: "Background Agent", desc: "Lightweight installer that runs quietly in the background and reports to your workspace. Sign in once and forget it. Best when your workspace administrator manages tracking centrally.", tags: ["Windows", "macOS", "Linux", ".exe / .dmg"] },
                ].map((c, i) => (
                  <motion.button
                    key={c.id}
                    type="button"
                    className={`dla-compare ${product === c.id ? "is-active" : ""}`}
                    onClick={() => setProduct(c.id)}
                    initial="hidden"
                    whileInView="show"
                    viewport={viewport}
                    custom={i}
                    variants={fadeUp}
                  >
                    <span className="dla-compare-badge">Selected</span>
                    <span className="dla-compare-ico"><c.Icon size={22} /></span>
                    <h3>{c.title}</h3>
                    <p>{c.desc}</p>
                    <div className="dla-compare-tags">{c.tags.map((t) => <span key={t}>{t}</span>)}</div>
                  </motion.button>
                ))}
              </div>
            </div>
          </section>

          {/* ── FEATURES ── */}
          <section className="dla-section dla-features">
            <div className="dla-wrap">
              <motion.div className="dla-section-head" initial="hidden" whileInView="show" viewport={viewport} variants={fadeUp}>
                <span className="dla-eyebrow">Why the app</span>
                <h2 className="dla-h2" style={{ marginTop: 10 }}>Built for real work</h2>
                <p className="dla-sub">Native power you don't get in the browser.</p>
              </motion.div>
              <div className="dla-feature-grid">
                {features.map((f, i) => (
                  <motion.div className="dla-feature" key={f.title} initial="hidden" whileInView="show" viewport={viewport} custom={i} variants={fadeUp}>
                    <div className="dla-feature-ico"><f.icon size={20} strokeWidth={2} /></div>
                    <div>
                      <h4>{f.title}</h4>
                      <p>{f.desc}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </section>

          {/* ── STEPS ── */}
          <section className="dla-section">
            <div className="dla-wrap">
              <motion.div className="dla-steps-head" initial="hidden" whileInView="show" viewport={viewport} variants={fadeUp}>
                <h2 className="dla-h2">Up and running in 3 steps</h2>
                <span className="dla-tag">{product === "desktop" ? "Desktop App" : "Background Agent"}</span>
              </motion.div>
              <div className="dla-steps">
                {steps.map((s, i) => (
                  <motion.div className="dla-step" key={s.n} initial="hidden" whileInView="show" viewport={viewport} custom={i} variants={fadeUp}>
                    <div className="dla-step-num">{s.n}</div>
                    <h3>{s.title}</h3>
                    <p>{s.desc}</p>
                  </motion.div>
                ))}
              </div>
            </div>
          </section>

          {/* ── SECURITY ── */}
          <section className="dla-security">
            <div className="dla-wrap">
              <div className="dla-security-inner">
                {security.map((s, i) => (
                  <motion.div className="dla-sec-item" key={s.title} initial="hidden" whileInView="show" viewport={viewport} custom={i} variants={fadeUp}>
                    <div className="dla-sec-ico"><s.icon size={22} strokeWidth={2} /></div>
                    <div>
                      <h4>{s.title}</h4>
                      <p>{s.sub}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </section>

          {/* ── FAQ ── */}
          <section className="dla-section dla-faq">
            <div className="dla-wrap">
              <motion.div className="dla-section-head" initial="hidden" whileInView="show" viewport={viewport} variants={fadeUp}>
                <span className="dla-eyebrow">Questions</span>
                <h2 className="dla-h2" style={{ marginTop: 10 }}>Frequently asked</h2>
              </motion.div>
              <div className="dla-faq-list">
                {faqs.map((f, i) => (
                  <div className={`dla-faq-item ${faqOpen[i] ? "is-open" : ""}`} key={i}>
                    <button className="dla-faq-q" onClick={() => toggleFaq(i)} aria-expanded={!!faqOpen[i]}>
                      {f.q}
                      <span className="dla-faq-icon">{faqOpen[i] ? <Minus size={18} /> : <Plus size={18} />}</span>
                    </button>
                    <AnimatePresence initial={false}>
                      {faqOpen[i] && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.28, ease: [0.2, 0.7, 0.2, 1] }}
                          style={{ overflow: "hidden" }}
                        >
                          <p className="dla-faq-a">{f.a}</p>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <ReadyToTransform
            title="Ready to Understand Your Team?"
            description="Join hundreds of teams using TeamGrid to make smarter decisions and build better workplaces."
            primaryButtonText="Register"
            primaryButtonLink="/register"
            secondaryButtonText="Talk to Sales"
            secondaryButtonLink="/contact-us"
            showAdditionalInfo={true}
          />
        </div>
        <Footer />
      </div>
    </>
  );
};

export default DownloadAllPage;
